import { useEffect, useRef } from "react";

/**
 * One-shot Leaply-logo firework (ported from the prototype).
 *
 * 1. Tight burst at the bottom of the page (all pieces explode within ~300 ms).
 * 2. Pieces shoot upward to a random point near the top of the viewport.
 * 3. They then drift slowly back down with leaf-like horizontal sway and
 *    gentle rotation, exiting below the viewport.
 *
 * Each piece runs `iterations: 1` with `fill: forwards`, so once the
 * firework plays out the pieces stay at their final (off-screen) state and
 * the page is quiet.
 */
// Three discrete piece sizes. Each gets its own fall-duration range so the
// screen ends up filled with logos at different heights at any given moment
// — small pieces fall fast, large ones drift slowly.
const SIZE_TIERS = [
  { size: 26, durationMin: 5000, durationMax: 7000 }, // small / fast
  { size: 44, durationMin: 8000, durationMax: 11000 }, // medium
  { size: 70, durationMin: 12000, durationMax: 16000 }, // large / slow
];

export function ConfettiLogos({ count = 120 }: { count?: number }) {
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const viewportW = window.innerWidth;
    const viewportH = window.innerHeight;

    const created: HTMLDivElement[] = [];

    for (let i = 0; i < count; i++) {
      // Round-robin through the three tiers so the mix is roughly even,
      // with small jitter so adjacent pieces don't look mechanically paired.
      const tier =
        SIZE_TIERS[(i + Math.floor(Math.random() * 3)) % SIZE_TIERS.length]!;
      const size = tier.size;
      const duration =
        tier.durationMin +
        Math.random() * (tier.durationMax - tier.durationMin);

      // Peak position — pieces spread across the full width and reach
      // the top 70–95 % of the viewport height.
      const peakX = (Math.random() - 0.5) * viewportW * 0.9;
      const peakY = -(viewportH * (0.7 + Math.random() * 0.25));

      // Leaf sway: three horizontal pivots between peak and the floor so the
      // descent zig-zags side to side.
      const swayAmp = 40 + Math.random() * 90; // 40–130 px
      const swaySign = Math.random() > 0.5 ? 1 : -1;
      const sway1 = peakX + swayAmp * swaySign;
      const sway2 = peakX - swayAmp * swaySign * 0.7;
      const sway3 = peakX + swayAmp * swaySign * 0.5;
      // Final landing horizontal — slight extra drift past last sway.
      const finalX = sway3 + (Math.random() - 0.5) * 80;
      // Off-screen below the viewport.
      const finalY = viewportH + 120;

      // Gentle rotation — 0.5 to 1.5 full turns over the lifetime.
      const spin = (Math.random() > 0.5 ? 1 : -1) * (180 + Math.random() * 360);

      const el = document.createElement("div");
      el.style.position = "absolute";
      el.style.left = `${-size / 2}px`;
      el.style.top = `${-size / 2}px`;
      el.style.width = `${size}px`;
      el.style.height = `${size}px`;
      el.style.backgroundImage = "url(/logomark.svg)";
      el.style.backgroundSize = "contain";
      el.style.backgroundRepeat = "no-repeat";
      el.style.backgroundPosition = "center";
      el.style.backgroundColor = "#ffffff";
      el.style.borderRadius = "50%";
      el.style.boxShadow = "0 2px 6px rgba(0,0,0,0.12)";
      el.style.willChange = "transform, opacity";
      el.style.pointerEvents = "none";
      el.style.opacity = "0";
      root.appendChild(el);
      created.push(el);

      // `duration` is set per-tier above (small=fast, large=slow).
      const delay = Math.random() * 250; // 0–250 ms — tight burst

      el.animate(
        [
          // Spawn invisible at the origin
          {
            transform: "translate(0px, 0px) scale(0)",
            opacity: 0,
            offset: 0,
          },
          // Pop in (very quick)
          {
            transform: "translate(0px, 0px) scale(1.2)",
            opacity: 1,
            offset: 0.02,
          },
          {
            transform: "translate(0px, 0px) scale(1)",
            opacity: 1,
            offset: 0.04,
          },
          // Fast rise — about 16% of the lifetime to reach the top.
          {
            transform: `translate(${peakX * 0.5}px, ${peakY * 0.55}px) rotate(${spin * 0.15}deg)`,
            opacity: 1,
            offset: 0.1,
          },
          {
            transform: `translate(${peakX}px, ${peakY}px) rotate(${spin * 0.25}deg)`,
            opacity: 1,
            offset: 0.18,
          },
          // Slow leaf-like descent with horizontal sway (18–95 % of lifetime).
          {
            transform: `translate(${sway1}px, ${peakY * 0.65}px) rotate(${spin * 0.4}deg)`,
            opacity: 1,
            offset: 0.4,
          },
          {
            transform: `translate(${sway2}px, ${peakY * 0.3}px) rotate(${spin * 0.6}deg)`,
            opacity: 1,
            offset: 0.6,
          },
          {
            transform: `translate(${sway3}px, ${peakY * 0.05}px) rotate(${spin * 0.8}deg)`,
            opacity: 1,
            offset: 0.78,
          },
          {
            transform: `translate(${finalX}px, ${finalY * 0.5}px) rotate(${spin * 0.95}deg)`,
            opacity: 1,
            offset: 0.92,
          },
          // Settle off-screen — fade out as it leaves.
          {
            transform: `translate(${finalX}px, ${finalY}px) rotate(${spin}deg)`,
            opacity: 0,
            offset: 1,
          },
        ],
        {
          duration,
          delay,
          iterations: 1,
          easing: "ease-out",
          fill: "forwards",
        },
      );
    }

    return () => {
      created.forEach((el) => el.remove());
    };
  }, [count]);

  return (
    <div
      aria-hidden
      className="fixed inset-0 pointer-events-none z-40"
      style={{ overflow: "visible" }}
    >
      <div
        ref={rootRef}
        style={{ position: "absolute", left: "50%", bottom: 0 }}
      />
    </div>
  );
}
